<template>
  <div class="bb-fspec" ref="rootRef">
    <button
      type="button"
      class="bb-fspec__trigger"
      :class="{ 'is-open': open, 'is-touring': touring }"
      :aria-expanded="open"
      aria-controls="bb-feature-spec"
      title="이 화면 도움말 (Ctrl+K)"
      @click="togglePanel"
    >
      <v-icon size="15">mdi-help-circle-outline</v-icon>
      <span class="bb-fspec__trigger-text">
        <strong>도움말</strong>
        <kbd v-if="!touring" class="bb-fspec__kbd">Ctrl K</kbd>
        <em v-else class="bb-fspec__tour-progress">안내 {{ tourIndex + 1 }}/{{ tourSteps.length }}</em>
      </span>
    </button>

    <transition name="bb-fspec-pop">
      <aside
        v-if="open"
        id="bb-feature-spec"
        class="bb-fspec__panel"
        role="dialog"
        aria-modal="true"
        aria-label="화면 기능정의서"
        @keydown="onPanelKeydown"
      >
        <header class="bb-fspec__head">
          <div>
            <p class="bb-fspec__eyebrow">처음이어도 OK · 화면 안내</p>
            <h2>{{ spec.title }}</h2>
          </div>
          <button type="button" class="bb-fspec__close" aria-label="닫기" @click="closePanel">
            <v-icon size="18">mdi-close</v-icon>
          </button>
        </header>

        <div class="bb-fspec__guide-cta">
          <div class="bb-fspec__guide-copy">
            <strong>처음 쓰시나요? 여기서 시작하세요</strong>
            <span>
              화면이 알아서 탭·창을 열고 「여기가 그 기능」을 밝게 가리킵니다.
              연습용 데이터가 없으면 만들어 줄지 물어봅니다.
            </span>
          </div>
          <div class="bb-fspec__guide-actions">
            <button
              type="button"
              class="bb-fspec__guide-btn is-primary"
              :disabled="tourBooting"
              @click="startTour(true)"
            >
              <v-icon size="16">mdi-play</v-icon>
              {{ tourBooting ? '준비 중…' : '자동으로 보기' }}
            </button>
            <button
              type="button"
              class="bb-fspec__guide-btn"
              :disabled="tourBooting"
              @click="startTour(false)"
            >
              <v-icon size="16">mdi-skip-next</v-icon>
              한 단계씩 보기
            </button>
          </div>
          <p class="bb-fspec__guide-tip">
            추천: 「자동으로 보기」 · 멈추려면 Esc
          </p>
        </div>

        <label class="bb-fspec__search">
          <v-icon size="16">mdi-magnify</v-icon>
          <input
            ref="searchInputRef"
            v-model.trim="query"
            type="search"
            placeholder="궁금한 말 검색 (예: 영수증, 마감, 업로드)"
            autocomplete="off"
            aria-label="기능 검색"
          />
          <button
            v-if="query"
            type="button"
            class="bb-fspec__search-clear"
            aria-label="검색어 지우기"
            @click="query = ''"
          >
            <v-icon size="16">mdi-close-circle</v-icon>
          </button>
        </label>

        <p class="bb-fspec__summary">{{ spec.summary }}</p>

        <section class="bb-fspec__section">
          <h3>이 화면에서 하는 일</h3>
          <p>{{ spec.purpose }}</p>
        </section>

        <section class="bb-fspec__section">
          <h3>
            기능 목록
            <em v-if="filteredFeatures.length !== spec.features.length">· {{ filteredFeatures.length }}건</em>
          </h3>
          <p class="bb-fspec__section-hint">
            이름을 누르면 그 자리로 데려다 줍니다. (창을 열 때는 한 번 물어봅니다)
          </p>
          <p v-if="query && !filteredFeatures.length" class="bb-fspec__empty">맞는 기능이 없습니다.</p>
          <ul class="bb-fspec__feature-list">
            <li
              v-for="(item, idx) in filteredFeatures"
              :key="item.name"
              class="bb-fspec__feature"
              :class="[
                `tone-${idx % 5}`,
                { 'has-target': !!item.target, 'is-guide': isGuideTarget(item.target) },
              ]"
            >
              <span class="bb-fspec__feature-idx">{{ idx + 1 }}</span>
              <button
                v-if="item.target"
                type="button"
                class="bb-fspec__feature-btn"
                @click="highlightFeature(item)"
              >
                <span class="bb-fspec__feature-top">
                  <strong>{{ item.name }}</strong>
                  <em v-if="isGuideTarget(item.target)" class="bb-fspec__badge">가이드</em>
                  <em v-else class="bb-fspec__badge is-soft">위치</em>
                </span>
                <span class="bb-fspec__feature-desc">{{ item.description }}</span>
              </button>
              <div v-else class="bb-fspec__feature-static">
                <span class="bb-fspec__feature-top">
                  <strong>{{ item.name }}</strong>
                </span>
                <span class="bb-fspec__feature-desc">{{ item.description }}</span>
              </div>
            </li>
          </ul>
        </section>

        <section v-if="spec.notes?.length" class="bb-fspec__section">
          <h3>참고</h3>
          <ul class="bb-fspec__notes">
            <li v-for="note in spec.notes" :key="note">{{ note }}</li>
          </ul>
        </section>
      </aside>
    </transition>

    <teleport to="body">
      <transition name="bb-fspec-tour-fade">
        <div
          v-if="touring && currentTourItem"
          class="bb-fspec-tour"
          :class="{ 'is-done': tourDoneShown }"
          role="dialog"
          aria-modal="true"
          :aria-label="`기능 가이드 ${tourIndex + 1}`"
        >
          <div class="bb-fspec-tour__dim" @click="onTourBackdrop" />
          <div
            class="bb-fspec-tour__spot"
            :class="{ 'is-missing': !spotRect }"
            :style="spotStyle"
          />
          <div
            :key="tourIndex"
            class="bb-fspec-tour__card"
            :style="cardStyle"
          >
            <div class="bb-fspec-tour__progress">
              <span
                class="bb-fspec-tour__progress-bar"
                :style="{ width: `${((tourIndex + 1) / Math.max(tourSteps.length, 1)) * 100}%` }"
              />
            </div>
            <div class="bb-fspec-tour__meta">
              <em>{{ tourIndex + 1 }} / {{ tourSteps.length }}</em>
              <span v-if="tourAuto" class="bb-fspec-tour__auto">자동</span>
              <span v-if="tourPreparing" class="bb-fspec-tour__auto">이동 중</span>
              <span v-if="!spotRect && !tourPreparing" class="bb-fspec-tour__warn">가까운 위치를 보여 주는 중</span>
            </div>
            <p v-if="tourStatus" class="bb-fspec-tour__status">{{ tourStatus }}</p>
            <p class="bb-fspec-tour__howto">밝게 표시된 곳을 보세요. 그게 지금 설명하는 기능입니다.</p>
            <h3>{{ currentTourItem.name }}</h3>
            <p>{{ currentTourItem.description }}</p>
            <div class="bb-fspec-tour__nav">
              <button type="button" class="bb-fspec-tour__btn is-ghost" @click="stopTour({ ask: true })">그만둘래요</button>
              <div class="bb-fspec-tour__nav-main">
                <button
                  type="button"
                  class="bb-fspec-tour__btn"
                  :disabled="tourIndex <= 0 || tourPreparing"
                  @click="prevTourStep"
                >
                  이전
                </button>
                <button
                  type="button"
                  class="bb-fspec-tour__btn is-primary"
                  :disabled="tourPreparing"
                  @click="nextTourStep"
                >
                  {{ tourIndex >= tourSteps.length - 1 ? '다 봤어요' : '다음으로' }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </transition>
    </teleport>

    <!-- 안내 완료 (커스텀, alert 대체) -->
    <teleport to="body">
      <transition name="bb-fspec-tour-fade">
        <div v-if="tourDoneShown" class="bb-fspec-done" role="dialog" aria-modal="true" aria-label="안내 완료">
          <div class="bb-fspec-done__dim" @click="tourDoneShown = false" />
          <div class="bb-fspec-done__card">
            <div class="bb-fspec-done__icon">
              <v-icon size="26">mdi-check-circle</v-icon>
            </div>
            <h3>화면 안내를 모두 봤습니다</h3>
            <p>다시 보고 싶으면 오른쪽 위 「도움말」을 눌러 주세요.<br>궁금한 기능 이름을 검색해도 됩니다.</p>
            <div class="bb-fspec-done__actions">
              <button type="button" class="bb-fspec-tour__btn" @click="tourDoneShown = false">닫기</button>
              <button type="button" class="bb-fspec-tour__btn is-primary" @click="restartFromDone">다시 보기</button>
            </div>
          </div>
        </div>
      </transition>
    </teleport>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue';
import type {
  BankbookGuideHost,
  BankbookTestFeatureItem,
  BankbookTestFeatureSpec,
} from '@/constants/bankbookTestFeatureSpec';

/** 화면 따라가기 핵심 순서 (탭·모달 전환 포함) */
const GUIDE_FLOW = [
  'bb-test-ym',
  'bb-test-banks',
  'bb-test-bank-manage',
  'bb-test-steps',
  'bb-test-summary',
  'bb-test-conditions',
  'bb-test-upload-quick',
  'bb-test-auto',
  'bb-test-ledger',
  'bb-test-account',
  'bb-test-recommend-settings',
  'bb-test-patterns',
  'bb-test-pairs',
  'bb-test-rel',
  'bb-test-funding',
  'bb-test-quick-edit',
  'bb-test-split',
  'bb-test-collection',
  'bb-test-collection-excel',
  'bb-test-receipt',
  'bb-test-analysis',
  'bb-test-closing',
] as const;

/** 모달/패널이 열렸을 때 우선 하이라이트 · 없으면 탭·버튼으로 친절히 폴백 */
const GUIDE_SPOT_FALLBACKS: Record<string, string[]> = {
  'bb-test-steps': [
    '[data-tour="bb-test-steps"]',
  ],
  'bb-test-summary': [
    '[data-tour="bb-test-summary"]',
    '[data-tour="bb-test-raw-tab"]',
  ],
  'bb-test-conditions': [
    '[data-tour="bb-test-conditions"]',
    '[data-tour="bb-test-raw-tab"]',
  ],
  'bb-test-ledger': [
    '[data-tour="bb-test-ledger"]',
    '[data-tour="bb-test-raw-tab"]',
  ],
  'bb-test-account': [
    '[data-tour="bb-test-account"]',
    '[data-tour="bb-test-raw-tab"]',
  ],
  'bb-test-rel': [
    '[data-tour="bb-test-rel"]',
    '[data-tour="bb-test-account"]',
    '[data-tour="bb-test-raw-tab"]',
  ],
  'bb-test-funding': [
    '[data-tour="bb-test-funding"]',
    '[data-tour="bb-test-account"]',
    '[data-tour="bb-test-raw-tab"]',
  ],
  'bb-test-quick-edit': [
    '[data-tour="bb-test-quick-edit"]',
    '[data-tour="bb-test-account"]',
    '[data-tour="bb-test-raw-tab"]',
  ],
  'bb-test-auto': [
    '[data-tour="bb-test-auto-dialog"]',
    '[data-tour="bb-test-auto"]',
  ],
  'bb-test-split': [
    '[data-tour="bb-test-split"]',
    '[data-tour="bb-test-account"]',
    '[data-tour="bb-test-raw-tab"]',
  ],
  'bb-test-closing': [
    '[data-tour="bb-test-closing"]',
    '[data-tour="bb-test-steps"]',
  ],
  'bb-test-heumieum': [
    '[data-tour="bb-test-heumieum"]',
    '[data-tour="bb-test-closing"]',
  ],
  'bb-test-patterns': [
    '[data-tour="bb-test-patterns-dialog"]',
    '[data-tour="bb-test-patterns"]',
    '[data-tour="bb-test-recommend-settings"]',
    '[data-tour="bb-test-account"]',
  ],
  'bb-test-pairs': [
    '[data-tour="bb-test-pairs-dialog"]',
    '[data-tour="bb-test-pairs"]',
    '[data-tour="bb-test-recommend-settings"]',
    '[data-tour="bb-test-account"]',
  ],
  'bb-test-recommend-settings': [
    '[data-tour="bb-test-recommend-panel"]',
    '[data-tour="bb-test-recommend-settings"]',
    '[data-tour="bb-test-account"]',
  ],
  'bb-test-upload-quick': [
    '[data-tour="bb-test-upload-dialog"]',
    '[data-tour="bb-test-upload-quick"]',
    '[data-tour="bb-test-raw-tab"]',
  ],
  'bb-test-bank-manage': [
    '[data-tour="bb-test-bank-manage-dialog"]',
    '[data-tour="bb-test-bank-manage"]',
    '[data-tour="bb-test-banks"]',
  ],
  'bb-test-collection-excel': [
    '[data-tour="bb-test-collection-excel-dialog"]',
    '[data-tour="bb-test-collection-excel"]',
    '[data-tour="bb-test-collection"]',
    '[data-tour="bb-test-collection-tab"]',
  ],
  'bb-test-labor': [
    '[data-tour="bb-test-labor-dialog"]',
    '[data-tour="bb-test-labor"]',
    '[data-tour="bb-test-summary"]',
  ],
  'bb-test-settlement': [
    '[data-tour="bb-test-settlement-dialog"]',
    '[data-tour="bb-test-settlement"]',
    '[data-tour="bb-test-raw-tab"]',
  ],
  'bb-test-receipt': [
    '[data-tour="bb-test-receipt"]',
    '[data-tour="bb-test-receipt-tab"]',
    '[data-tour="bb-test-steps"]',
  ],
  'bb-test-collection': [
    '[data-tour="bb-test-collection"]',
    '[data-tour="bb-test-collection-tab"]',
    '[data-tour="bb-test-steps"]',
  ],
  'bb-test-analysis': [
    '[data-tour="bb-test-analysis"]',
    '[data-tour="bb-test-analysis-tab"]',
    '[data-tour="bb-test-steps"]',
  ],
};

const GUIDE_SET = new Set<string>(GUIDE_FLOW);
const AUTO_STEP_MS = 3200;
const SPOT_PAD = 8;

const props = defineProps<{
  spec: BankbookTestFeatureSpec;
  guideHost?: BankbookGuideHost | null;
}>();

const open = ref(false);
const query = ref('');
const rootRef = ref<HTMLElement | null>(null);
const searchInputRef = ref<HTMLInputElement | null>(null);

const touring = ref(false);
const tourAuto = ref(false);
const tourBooting = ref(false);
const tourPreparing = ref(false);
const tourIndex = ref(0);
const tourSteps = ref<BankbookTestFeatureItem[]>([]);
const tourStatus = ref('');
const tourDoneShown = ref(false);
const spotExtraSelectors = ref<string[]>([]);
const spotRect = ref<DOMRect | null>(null);
const cardPos = ref({ top: 24, left: 24 });

let highlightTimer: ReturnType<typeof setTimeout> | null = null;
let autoTimer: ReturnType<typeof setTimeout> | null = null;
let spotRaf = 0;
let tourSeq = 0;

const filteredFeatures = computed(() => {
  const q = query.value.trim().toLowerCase();
  const list = props.spec.features || [];
  if (!q) return list;
  return list.filter((item) => {
    const bag = [item.name, item.description, ...(item.keywords || [])].join(' ').toLowerCase();
    return bag.includes(q);
  });
});

const currentTourItem = computed(() => tourSteps.value[tourIndex.value] || null);

const spotStyle = computed(() => {
  const r = spotRect.value;
  if (!r) {
    return {
      top: '40%',
      left: '50%',
      width: '0px',
      height: '0px',
      opacity: '0',
    };
  }
  return {
    top: `${Math.max(4, r.top - SPOT_PAD)}px`,
    left: `${Math.max(4, r.left - SPOT_PAD)}px`,
    width: `${r.width + SPOT_PAD * 2}px`,
    height: `${r.height + SPOT_PAD * 2}px`,
    opacity: '1',
  };
});

const cardStyle = computed(() => ({
  top: `${cardPos.value.top}px`,
  left: `${cardPos.value.left}px`,
}));

const isGuideTarget = (target?: string) => !!target && GUIDE_SET.has(target);

watch(open, async (value) => {
  if (!value) return;
  await nextTick();
  if (window.matchMedia('(pointer: fine)').matches) {
    searchInputRef.value?.focus();
  }
});

const clearHighlight = () => {
  document.querySelectorAll('.bb-fspec-pulse').forEach((el) => el.classList.remove('bb-fspec-pulse'));
  if (highlightTimer) {
    clearTimeout(highlightTimer);
    highlightTimer = null;
  }
};

const clearAuto = () => {
  if (autoTimer) {
    clearTimeout(autoTimer);
    autoTimer = null;
  }
};

const stopSpotWatch = () => {
  if (spotRaf) {
    cancelAnimationFrame(spotRaf);
    spotRaf = 0;
  }
  window.removeEventListener('resize', scheduleSpotUpdate);
  window.removeEventListener('scroll', scheduleSpotUpdate, true);
};

const placeCard = (rect: DOMRect | null) => {
  const cardW = Math.min(360, window.innerWidth - 24);
  const cardH = 230;
  const pad = 14;
  if (!rect) {
    cardPos.value = {
      top: Math.max(pad, (window.innerHeight - cardH) / 2),
      left: Math.max(pad, (window.innerWidth - cardW) / 2),
    };
    return;
  }
  let top = rect.bottom + pad + SPOT_PAD;
  let left = Math.min(Math.max(pad, rect.left), window.innerWidth - cardW - pad);
  if (top + cardH > window.innerHeight - pad) {
    top = Math.max(pad, rect.top - cardH - pad - SPOT_PAD);
  }
  if (top < pad) top = pad;
  // 카드가 스포트라이트와 너무 겹치면 오른쪽으로
  if (
    left < rect.right &&
    left + cardW > rect.left &&
    top < rect.bottom &&
    top + cardH > rect.top
  ) {
    const right = rect.right + pad;
    if (right + cardW < window.innerWidth - pad) left = right;
  }
  cardPos.value = { top, left };
};

const resolveSpotEl = (target: string): HTMLElement | null => {
  const selectors = [
    ...spotExtraSelectors.value,
    ...(GUIDE_SPOT_FALLBACKS[target] || []),
    `[data-tour="${target}"]`,
  ];
  for (const sel of selectors) {
    if (!sel) continue;
    const el = document.querySelector(sel) as HTMLElement | null;
    if (!el) continue;
    const rect = el.getBoundingClientRect();
    // display:none / 아직 레이아웃 전(0크기)은 스킵
    if (rect.width < 2 && rect.height < 2) continue;
    return el;
  }
  return null;
};

const waitForSpotEl = async (target: string, timeoutMs = 800): Promise<HTMLElement | null> => {
  const started = performance.now();
  let el = resolveSpotEl(target);
  while (!el && performance.now() - started < timeoutMs) {
    await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
    el = resolveSpotEl(target);
  }
  return el;
};

const updateSpot = () => {
  const item = currentTourItem.value;
  if (!item?.target) {
    spotRect.value = null;
    placeCard(null);
    return;
  }
  const el = resolveSpotEl(item.target);
  if (!el) {
    spotRect.value = null;
    placeCard(null);
    return;
  }
  const rect = el.getBoundingClientRect();
  spotRect.value = rect;
  placeCard(rect);
};

const scheduleSpotUpdate = () => {
  if (spotRaf) cancelAnimationFrame(spotRaf);
  spotRaf = requestAnimationFrame(() => {
    spotRaf = 0;
    updateSpot();
  });
};

const prepareAndFocus = async (seq: number) => {
  tourPreparing.value = true;
  const item = currentTourItem.value;
  spotExtraSelectors.value = [];
  try {
    if (item?.target && props.guideHost?.prepare) {
      const result = await props.guideHost.prepare(item.target);
      if (seq !== tourSeq) return;
      if (result?.spotSelectors?.length) {
        spotExtraSelectors.value = result.spotSelectors;
      }
      tourStatus.value = result?.status || '';
    }
    await nextTick();
    if (seq !== tourSeq) return;
    const el = item?.target ? await waitForSpotEl(item.target) : null;
    if (seq !== tourSeq) return;
    el?.scrollIntoView({ behavior: 'auto', block: 'center', inline: 'nearest' });
    updateSpot();
  } finally {
    if (seq === tourSeq) tourPreparing.value = false;
  }
};

const finishTour = () => {
  stopTour();
  tourDoneShown.value = true;
  props.guideHost?.notify?.('안내를 모두 봤습니다. 잘하셨어요!', 'success');
};

const scheduleAutoAdvance = () => {
  clearAuto();
  if (!touring.value || !tourAuto.value) return;
  autoTimer = setTimeout(() => {
    void (async () => {
      if (tourIndex.value >= tourSteps.value.length - 1) {
        finishTour();
        return;
      }
      tourIndex.value += 1;
      const seq = ++tourSeq;
      await prepareAndFocus(seq);
      scheduleAutoAdvance();
    })();
  }, AUTO_STEP_MS);
};

const buildTourSteps = (): BankbookTestFeatureItem[] => {
  const byTarget = new Map(
    (props.spec.features || [])
      .filter((f) => f.target)
      .map((f) => [f.target as string, f])
  );
  const steps: BankbookTestFeatureItem[] = [];
  for (const target of GUIDE_FLOW) {
    const item = byTarget.get(target);
    if (item) steps.push(item);
  }
  return steps;
};

const restartFromDone = () => {
  tourDoneShown.value = false;
  void startTour(false);
};

const startTour = async (auto: boolean) => {
  const steps = buildTourSteps();
  if (!steps.length) return;

  tourDoneShown.value = false;
  clearHighlight();
  clearAuto();
  tourBooting.value = true;
  tourStatus.value = '';
  props.guideHost?.notify?.('안내를 준비하는 중이에요…', 'info');

  try {
    if (props.guideHost?.ensureData) {
      const ensured = await props.guideHost.ensureData();
      if (ensured.message) tourStatus.value = ensured.message;
      if (ensured.cancelled) {
        props.guideHost.notify?.('안내를 취소했습니다.', 'warn');
        return;
      }
      if (!ensured.ok) {
        window.alert(
          ensured.message ||
            '지금은 안내를 시작할 수 없습니다.\n통장이 등록돼 있는지 확인해 주세요.'
        );
        props.guideHost.notify?.(ensured.message || '안내를 시작할 수 없습니다.', 'warn');
        return;
      }
    }
  } finally {
    tourBooting.value = false;
  }

  open.value = false;
  query.value = '';
  tourSteps.value = steps;
  tourIndex.value = 0;
  tourAuto.value = auto;
  touring.value = true;
  window.addEventListener('resize', scheduleSpotUpdate);
  window.addEventListener('scroll', scheduleSpotUpdate, true);
  props.guideHost?.notify?.('밝은 부분을 따라가 보세요.', 'success');
  const seq = ++tourSeq;
  await prepareAndFocus(seq);
  if (auto) scheduleAutoAdvance();
};

const stopTour = (opts?: { ask?: boolean }) => {
  // opts.ask는 더 이상 confirm을 띄우지 않고 즉시 종료 (UX: 비차단)
  void opts;
  const wasTouring = touring.value;
  tourSeq += 1;
  touring.value = false;
  tourAuto.value = false;
  tourPreparing.value = false;
  tourBooting.value = false;
  tourIndex.value = 0;
  tourSteps.value = [];
  spotRect.value = null;
  spotExtraSelectors.value = [];
  tourStatus.value = '';
  clearAuto();
  stopSpotWatch();
  props.guideHost?.cleanup?.();
  if (wasTouring && opts?.ask) {
    props.guideHost?.notify?.('안내를 멈췄습니다. 언제든 다시 볼 수 있어요.', 'info');
  }
};

const nextTourStep = () => {
  tourAuto.value = false;
  clearAuto();
  if (tourIndex.value >= tourSteps.value.length - 1) {
    finishTour();
    return;
  }
  tourIndex.value += 1;
  const seq = ++tourSeq;
  void prepareAndFocus(seq);
};

const prevTourStep = () => {
  if (tourIndex.value <= 0) return;
  tourAuto.value = false;
  clearAuto();
  tourIndex.value -= 1;
  const seq = ++tourSeq;
  void prepareAndFocus(seq);
};

const onTourBackdrop = () => {
  if (tourPreparing.value) return;
  nextTourStep();
};

const togglePanel = () => {
  if (touring.value) {
    stopTour({ ask: true });
    if (touring.value) return;
  }
  open.value = !open.value;
  if (!open.value) query.value = '';
};

const closePanel = () => {
  open.value = false;
  query.value = '';
};

const highlightFeature = async (item: BankbookTestFeatureItem) => {
  if (!item.target) return;
  clearHighlight();
  closePanel();
  spotExtraSelectors.value = [];
  props.guideHost?.notify?.(`「${item.name}」 자리를 보여 드릴게요.`, 'info');
  if (props.guideHost?.prepare) {
    const result = await props.guideHost.prepare(item.target);
    if (result?.spotSelectors?.length) spotExtraSelectors.value = result.spotSelectors;
  }
  await nextTick();
  const el = await waitForSpotEl(item.target);
  if (!el) {
    props.guideHost?.notify?.('화면에서 위치를 찾지 못했습니다.', 'warn');
    return;
  }
  el.scrollIntoView({ behavior: 'auto', block: 'center', inline: 'nearest' });
  el.classList.add('bb-fspec-pulse');
  highlightTimer = setTimeout(() => {
    el.classList.remove('bb-fspec-pulse');
    highlightTimer = null;
    props.guideHost?.cleanup?.();
  }, 1800);
};

const onPanelKeydown = (e: KeyboardEvent) => {
  if (e.key === 'Escape') {
    e.stopPropagation();
    closePanel();
  }
};

const onDocPointer = (e: MouseEvent) => {
  if (!open.value || !rootRef.value) return;
  if (!rootRef.value.contains(e.target as Node)) closePanel();
};

const onGlobalKey = (e: KeyboardEvent) => {
  if (touring.value) {
    if (e.key === 'Escape') {
      e.preventDefault();
      stopTour({ ask: true });
      return;
    }
    if (e.key === 'ArrowRight' || e.key === 'Enter') {
      e.preventDefault();
      nextTourStep();
      return;
    }
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      prevTourStep();
      return;
    }
  }
  if (!(e.ctrlKey || e.metaKey) || e.key.toLowerCase() !== 'k') return;
  const path = String(window.location.pathname || '');
  // 정식(bankbook) · (구)테스트(bankbook-test) 주소 모두 허용
  if (!path.includes('bankbook')) return;
  e.preventDefault();
  togglePanel();
};

onMounted(() => {
  document.addEventListener('mousedown', onDocPointer);
  window.addEventListener('keydown', onGlobalKey);
});

onUnmounted(() => {
  document.removeEventListener('mousedown', onDocPointer);
  window.removeEventListener('keydown', onGlobalKey);
  clearHighlight();
  stopTour();
});
</script>
